import { Box, Paper, Typography, Button } from "@mui/material";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import InputField from "../../components/common/InputField";
import SelectField from "../../components/common/SelectField";
import Section from "../../components/common/Section";
import UploadCard from "../../components/lead/UploadCard";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import { getLeadById, saveLoanDetails } from "../../api/api";
import { formatCurrency, limitDigits, onlyDigits } from "../../utils/format";
import { handleLeadRedirect } from "../../utils/leadRedirect";

const AddCredit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState<any>({});
  const [isView, setIsView] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  // 🔥 LOAD DATA
  const fetchData = async () => {
    if (!id) return;

    const res = await getLeadById(id);
    const lead = res.data?.data;
    if (!["credit", "approved", "disbursed"].includes(lead.status)) {
      handleLeadRedirect(lead, navigate);
      return;
    }
    if (lead.loan) {
      setIsView(true);
    }

    if (lead.loan) {
      setForm({
        ...lead.loan,

        dueDate: lead.loan?.dueDate
          ? new Date(lead.loan.dueDate).toISOString().split("T")[0]
          : "",

        schemeFile: lead.loan.schemeFile || null,

        existingSchemeFile: lead.loan.schemeFile || null,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const minDate = tomorrow.toISOString().split("T")[0];

  useEffect(() => {
    if (isView) return;

    const loan = Number(form.loanAmount || 0);
    const gap = Number(form.gapInterest || 0);
    const file = Number(form.fileCharges || 0);

    const net = loan - (gap + file);

    setForm((prev: any) => ({
      ...prev,
      netAmount: net > 0 ? net : 0,
    }));
  }, [form.loanAmount, form.gapInterest, form.fileCharges, isView]);

  // 🔥 CHANGE
  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));

    // 🔥 REMOVE ERROR WHEN USER TYPES
    setErrors((prev: any) => ({
      ...prev,
      [key]: "",
    }));
  };

  // 🔥 VALIDATION
  const validateForm = () => {
    const errors: any = {};

    if (!form.schemeType) errors.schemeType = "Select scheme";

    if (!form.loanAmount) errors.loanAmount = "Enter loan amount";
    else if (isNaN(form.loanAmount)) errors.loanAmount = "Must be number";

    if (!form.tenure) errors.tenure = "Enter tenure";
    else if (isNaN(form.tenure)) errors.tenure = "Must be number";

    if (!form.roi) errors.roi = "Enter ROI";
    else if (isNaN(form.roi)) errors.roi = "Must be number";

    if (!form.dueDate) errors.dueDate = "Select due date";

    if (!form.gapInterest) errors.gapInterest = "Enter gap interest";
    else if (isNaN(form.gapInterest)) errors.gapInterest = "Must be number";

    if (!form.fileCharges) errors.fileCharges = "Enter file charges";
    else if (isNaN(form.fileCharges)) errors.fileCharges = "Must be number";

    if (!form.netAmount) errors.netAmount = "Enter net disbursement";
    else if (isNaN(form.netAmount)) errors.netAmount = "Must be number";

    // 🔥 FILE VALIDATION (EDIT SAFE)
    if (!form.schemeFile && !form.existingSchemeFile) {
      errors.schemeFile = "Upload document (DO / Sanction)";
    }
    if (form.dueDate) {
      const selected = new Date(form.dueDate);
      const today = new Date();

      // today reset time
      today.setHours(0, 0, 0, 0);

      if (selected <= today) {
        errors.dueDate = "EMI date must be in future";
      }
    }

    return errors;
  };

  // 🔥 SUBMIT
  const handleSubmit = async () => {
    if (!id) return;

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      const firstErrorKey = Object.keys(validationErrors)[0];
      document.querySelector(`[name="${firstErrorKey}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      Object.keys(form).forEach((key) => {
        const value = form[key];

        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value ?? "");
        }
      });

      await saveLoanDetails(id, formData);

      dispatch(
        showSnackbar({
          message: "Credit saved successfully",
          type: "success",
        }),
      );

      navigate("/loans");
    } catch (err) {
      console.log(err);
      dispatch(
        showSnackbar({
          message: "Failed to save credit",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
          Credit / Bank Details
        </Typography>

        <Section title="Loan Details" />

        {/* ROW 1 */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <SelectField
            name="schemeType"
            label="Scheme"
            value={form.schemeType}
            onChange={(e: any) => handleChange("schemeType", e.target.value)}
            options={[
              { label: "DO (Delivery Order)", value: "DO" },
              { label: "Sanction Letter", value: "SANCTION" },
            ]}
            error={!!errors.schemeType}
            helperText={errors.schemeType}
            disabled={isView}
          />

          <InputField
            label="Loan Amount"
            placeholder="Enter loan amount"
            type="tel"
            value={form.loanAmount}
            onChange={(e: any) => {
              let value = onlyDigits(e.target.value);
              value = limitDigits(value, 9);

              handleChange("loanAmount", value);
            }}
            error={!!errors.loanAmount}
            helperText={
              errors.loanAmount
                ? errors.loanAmount
                : formatCurrency(form.loanAmount)
            }
            disabled={isView}
          />

          <InputField
            label="Tenure (Months)"
            placeholder="e.g. 36"
            value={form.tenure}
            onChange={(e: any) => handleChange("tenure", e.target.value)}
            error={!!errors.tenure}
            helperText={errors.tenure}
            disabled={isView}
          />
        </Box>

        {/* ROW 2 */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <InputField
            label="Rate of Interest (%)"
            placeholder="e.g. 10.5"
            value={form.roi}
            onChange={(e: any) => handleChange("roi", e.target.value)}
            error={!!errors.roi}
            helperText={errors.roi}
            disabled={isView}
          />

          <InputField
            type="date"
            label="1st EMI Date"
            value={form.dueDate || ""}
            onChange={(e: any) => handleChange("dueDate", e.target.value)}
            inputProps={{
              min: minDate, // 🔥 past + today block
            }}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
            disabled={isView}
          />
          <InputField
            label="Gap Interest"
            placeholder="Enter amount"
            type="tel"
            value={form.gapInterest}
            onChange={(e: any) => {
              let value = onlyDigits(e.target.value);
              value = limitDigits(value, 9);
              handleChange("gapInterest", value);
            }}
            error={!!errors.gapInterest}
            helperText={
              errors.gapInterest
                ? errors.gapInterest
                : formatCurrency(form.gapInterest)
            }
            disabled={isView}
          />
        </Box>

        {/* ROW 3 */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <InputField
            label="File Charges"
            placeholder="Enter charges"
            type="tel"
            value={form.fileCharges}
            onChange={(e: any) => {
              let value = onlyDigits(e.target.value);
              value = limitDigits(value, 9);
              handleChange("fileCharges", value);
            }}
            error={!!errors.fileCharges}
            helperText={
              errors.fileCharges
                ? errors.fileCharges
                : formatCurrency(form.fileCharges)
            }
            disabled={isView}
          />

          <InputField
            label="Net Amount (After Bank Deduction)"
            placeholder="Enter amount"
            value={form.netAmount}
            disabled // 🔥 important
            helperText={formatCurrency(form.netAmount)}
          />

          <Box sx={{ flex: 1 }} />
        </Box>

        {/* 🔥 FILE UPLOAD */}
        <Section title="Upload Document" />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ width: 200 }}>
            <UploadCard
              label="Upload DO / Sanction Letter"
              name="schemeFile"
              file={form.schemeFile}
              existingFile={form.existingSchemeFile}
              onChange={(name, file) => handleChange(name, file)}
              error={errors.schemeFile}
            />
          </Box>
        </Box>

        {/* BUTTON */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{ borderRadius: "12px" }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          {!isView && (
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                px: 3,
                background:
                  "linear-gradient(135deg, var(--primary), var(--primary-light))",
              }}
              onClick={handleSubmit}
            >
              {loading
                ? "Processing..."
                : form && Object.keys(form).length > 0
                  ? "Update Credit"
                  : "Save Credit"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AddCredit;
